import { readonly, ref, type Ref } from 'vue';

/**
 * AB#22137 — tablet layout: 768px to 1024px wide, the touch-friendly desktop.
 * It starts where the phone layout stops (usePhone, below 768px) and ends at
 * 1024px, so a desktop wider than that is exactly what it was before.
 *
 * In this range the desktop layout stays, with three changes: the open
 * navigation rail floats over the page instead of pushing it, an open order
 * takes the whole width (the list is one tap away), and on a touch screen
 * the controls are finger-sized (assets/tablet.scss).
 *
 * A module-level singleton like usePhone: one media-query listener for the app.
 */
export const TABLET_QUERY = '(min-width: 768px) and (max-width: 1024px)';

const tablet = ref(false);
let started = false;

const start = (): void => {
  if (started) return;
  started = true;
  try {
    const mq = window.matchMedia(TABLET_QUERY);
    tablet.value = mq.matches;
    const onChange = (e: MediaQueryListEvent): void => {
      tablet.value = e.matches;
    };
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onChange);
    else mq.addListener?.(onChange);
  } catch {
    // No window or no matchMedia (tests): not a tablet.
    tablet.value = false;
  }
};

export const useTablet = (): Readonly<Ref<boolean>> => {
  start();
  return readonly(tablet);
};

/** For tests: set the flag directly. */
export const __setTabletForTests = (value: boolean): void => {
  started = true;
  tablet.value = value;
};
